import { useEffect, useMemo } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isDevelopment = import.meta.env.DEV;
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const isResponseError = isRouteErrorResponse(error);
  const errorTitle = isResponseError
    ? `Error ${error.status}: ${error.statusText}`
    : t("COMPONENTS.ERROR_BOUNDARY.TITLE");
  const errorMessage = useMemo(() => {
    if (isResponseError) {
      const responseMessage =
        typeof error.data?.message === "string"
          ? (error.data.message as string)
          : "";
      const trimmedResponseMessage = responseMessage.trim();
      return trimmedResponseMessage || t("COMPONENTS.ERROR_BOUNDARY.MESSAGE");
    }

    if (error instanceof Error) {
      const trimmedErrorMessage = error.message.trim();
      return trimmedErrorMessage || t("COMPONENTS.ERROR_BOUNDARY.MESSAGE");
    }

    return t("COMPONENTS.ERROR_BOUNDARY.MESSAGE");
  }, [error, isResponseError, t]);
  const errorStack = isResponseError
    ? typeof error.data?.stack === "string"
      ? error.data.stack
      : null
    : error instanceof Error
      ? error.stack
      : null;

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-8">
      <h1 className="text-4xl font-bold text-red-600 mb-4">{errorTitle}</h1>
      <p className="text-gray-600 text-center max-w-md">{errorMessage}</p>
      {isDevelopment && errorStack && (
        <details className="mt-4 w-full max-w-4xl">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            {t("COMPONENTS.ERROR_BOUNDARY.SHOW_STACK")}
          </summary>
          <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto max-h-64 text-left">
            {errorStack}
          </pre>
        </details>
      )}
      <button
        onClick={handleReload}
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        {t("COMPONENTS.ERROR_BOUNDARY.RELOAD")}
      </button>
    </div>
  );
};
